import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../../assets/css/Cartattva.css";

import cartattvaLogo from "../../assets/img/clients/Cartattva logo.png";
import caseStudyImage from "../../assets/cartattva-case-study.png";
import inspectionReport from "../../assets/img/Cartattva/Report_904.pdf";

// Adjust this path to wherever analytics.js actually lives in your project
import {
  trackCaseStudyView,
  trackQuoteRequest,
  trackEvent,
} from "../../utils/analytics";

/* =========================================================
   SCREENSHOT TITLES
   IMPORTANT:
   These names must match the actual files in:

   src/assets/img/Cartattva
========================================================= */

const screenshotTitles = {
  "Appointment Management.png": "Appointment Management",
  "Lead Management.png": "Lead Management",
  "Customer Management.png": "Customer Management",
  "Dealer Management.png": "Dealer Management",
  "Inspection Reports.png": "Inspection Reports",
  "Business Statistics.png": "Business Statistics",
  "KPI Analytics Dashboard.png": "KPI & Analytics Dashboard",
  "Transactions.png": "Transactions",
};

/* =========================================================
   DYNAMICALLY LOAD ALL CARTATTVA IMAGES
========================================================= */

const cartattvaImages = require.context(
  "../../assets/img/Cartattva",
  false,
  /\.(png|jpe?g|jpeg|webp|gif)$/i
);

const screenshotData = cartattvaImages
  .keys()
  .map((file) => {
    const fileName = file.replace("./", "");

    return {
      fileName,
      image: cartattvaImages(file),
      title:
        screenshotTitles[fileName] ||
        fileName
          .replace(/\.[^/.]+$/, "")
          .replace(/[-_]/g, " ")
          .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    };
  });

/* =========================================================
   TECHNOLOGY
========================================================= */

const technologies = [
  "ASP.NET MVC",
  "React Native",
  "SQL Server",
  "SQLite",
  "REST APIs",
];

/* =========================================================
   BUSINESS IMPACT
========================================================= */

const businessImpact = [
  "Centralized customer, dealer and vehicle information",
  "Reduced manual work across inspection operations",
  "Offline capability for field evaluators",
  "Faster inspection report generation",
  "Structured inspection approval workflow",
  "Improved visibility into leads and follow-ups",
  "Better management visibility through KPI dashboards",
  "Connected sales and inspection operations",
];

/* =========================================================
   KEY FEATURES
========================================================= */

const features = [
  {
    number: "01",
    title: "Customer & Dealer Management",
    text: "Centralized management of customers, dealers and their complete business relationships.",
  },
  {
    number: "02",
    title: "Lead & Follow-up Management",
    text: "Capture leads, manage follow-ups and track the sales journey from enquiry to conversion.",
  },
  {
    number: "03",
    title: "Appointment Management",
    text: "Schedule vehicle inspections and manage appointments with evaluator assignment.",
  },
  {
    number: "04",
    title: "Evaluator Mobile Application",
    text: "A dedicated mobile application that enables evaluators to perform vehicle inspections directly from the field.",
  },
  {
    number: "05",
    title: "Offline Inspection & Sync",
    text: "Continue inspections without internet connectivity and automatically synchronize collected data when connectivity is restored.",
  },
  {
    number: "06",
    title: "Inspection & Approval Workflow",
    text: "Structured inspection submission with multi-level review and approval before final report generation.",
  },
  {
    number: "07",
    title: "Automated Inspection Reports",
    text: "Generate professional vehicle inspection reports with inspection data and photographs for customer delivery.",
  },
  {
    number: "08",
    title: "Sales & Pricing Management",
    text: "Sales teams can manage leads, follow-ups and vehicle pricing through a centralized sales workflow.",
  },
  {
    number: "09",
    title: "KPI & Business Dashboard",
    text: "Management dashboards provide visibility into leads, follow-ups, appointments, inspections and overall business activity.",
  },
];

/* =========================================================
   SCREENSHOT ORDER
========================================================= */

const preferredScreenshotOrder = [
  "Appointment Management.png",
  "Lead Management.png",
  "Customer Management.png",
  "Dealer Management.png",
  "Inspection Reports.png",
  "Business Statistics.png",
  "KPI Analytics Dashboard.png",
  "Transactions.png",
];

const orderedScreenshots = [
  ...preferredScreenshotOrder
    .map((fileName) =>
      screenshotData.find((item) => item.fileName === fileName)
    )
    .filter(Boolean),

  ...screenshotData.filter(
    (item) => !preferredScreenshotOrder.includes(item.fileName)
  ),
];

/* =========================================================
   COMPONENT
========================================================= */

export default function Cartattva() {
  const siteUrl = "https://hexxatech.co.in";
  const pageUrl = `${siteUrl}/case-studies/cartattva`;

  const title =
    "CarTattva Vehicle Inspection & Automotive Business Management Platform | HexxaTech";

  const description =
    "Explore how HexxaTech Softwares developed CarTattva, a custom automotive software platform for vehicle inspections, dealer management, leads, appointments, evaluator operations, approvals, reporting and sales workflows.";

  // Fire a case-study-view event once, when this page mounts
  useEffect(() => {
    trackCaseStudyView("CarTattva");
  }, []);

  /* =======================================================
     STRUCTURED DATA
  ======================================================= */

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: title,
        description: description,
        isPartOf: {
          "@type": "WebSite",
          "@id": `${siteUrl}#website`,
          url: siteUrl,
          name: "HexxaTech Softwares",
        },
        about: {
          "@type": "SoftwareApplication",
          name: "CarTattva",
          applicationCategory:
            "BusinessApplication",
          operatingSystem:
            "Web, Android",
        },
      },

      {
        "@type": "SoftwareApplication",
        "@id": `${pageUrl}#software`,
        name: "CarTattva",
        alternateName:
          "CarTattva Vehicle Inspection & Automotive Business Management Platform",
        description:
          "A custom automotive business management and vehicle inspection platform connecting customers, dealers, leads, appointments, evaluators, inspections, approvals, reports and sales workflows.",
        applicationCategory:
          "BusinessApplication",
        operatingSystem:
          "Web, Android",
        creator: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: siteUrl,
        },
      },

      {
        "@type": "Organization",
        "@id": `${siteUrl}#organization`,
        name: "HexxaTech Softwares",
        url: siteUrl,
      },

      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Case Studies",
            item: `${siteUrl}/case-studies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "CarTattva",
            item: pageUrl,
          },
        ],
      },

      {
        "@type": "ImageObject",
        contentUrl: cartattvaLogo,
        name: "CarTattva Logo",
        description:
          "CarTattva automotive vehicle inspection platform logo",
      },
    ],
  };

  return (
    <>
      {/* =====================================================
          SEO
      ====================================================== */}

      <title>{title}</title>

      <meta
        name="description"
        content={description}
      />

      <meta
        name="keywords"
        content="CarTattva, vehicle inspection software, automotive software development, vehicle inspection management system, automotive CRM, dealer management software, vehicle inspection app, automotive business management software, custom software development Pune"
      />

      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <link
        rel="canonical"
        href={pageUrl}
      />

      {/* =====================================================
          OPEN GRAPH
      ====================================================== */}

      <meta
        property="og:title"
        content="CarTattva Vehicle Inspection & Automotive Business Management Platform"
      />

      <meta
        property="og:description"
        content="A custom automotive software platform developed by HexxaTech Softwares for vehicle inspections, dealer management, leads, appointments, evaluator operations, approvals and reporting."
      />

      <meta
        property="og:url"
        content={pageUrl}
      />

      <meta
        property="og:type"
        content="article"
      />

      <meta
        property="og:site_name"
        content="HexxaTech Softwares"
      />

      <meta
        property="og:image"
        content={caseStudyImage}
      />

      <meta
        property="og:image:alt"
        content="CarTattva Vehicle Inspection Software Case Study"
      />

      {/* =====================================================
          TWITTER
      ====================================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content="CarTattva Vehicle Inspection Software | HexxaTech"
      />

      <meta
        name="twitter:description"
        content="Explore the CarTattva automotive vehicle inspection and business management platform developed by HexxaTech Softwares."
      />

      <meta
        name="twitter:image"
        content={caseStudyImage}
      />

      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      {/* =====================================================
          PAGE
      ====================================================== */}

      <main className="cartattva-page">

        {/* =================================================
            HERO
        ================================================== */}

        <section className="cartattva-hero">

          <div className="cartattva-hero-glow cartattva-glow-one" />
          <div className="cartattva-hero-glow cartattva-glow-two" />

          <div className="cartattva-container">

            <div className="cartattva-back">
              <Link to="/case-studies">
                ← Back to Case Studies
              </Link>
            </div>

            <div className="cartattva-hero-content">

              <div className="cartattva-logo-wrapper">
                <img
                  src={cartattvaLogo}
                  alt="CarTattva Vehicle Inspection Platform"
                  className="cartattva-logo"
                />
              </div>

              <span className="cartattva-eyebrow">
                <span className="cartattva-eyebrow-dot" />
                Automotive Case Study
              </span>

              <h1>
                CarTattva Vehicle Inspection &
                <br />
                Automotive Business
                <br />
                Management Platform
              </h1>

              <div className="cartattva-title-line" />

              <h2>
                A custom automotive software platform
                developed by HexxaTech Softwares
              </h2>

              <p>
                A complete automotive business management
                platform connecting customers, dealers,
                leads, appointments, vehicle inspections,
                evaluator operations, approvals, reporting
                and sales workflows in one centralized system.
              </p>

              <div className="cartattva-hero-tags">
                <span>Web + Mobile</span>
                <span>Offline-first Inspection</span>
                <span>Automated Reporting</span>
                <span>HexxaTech Softwares</span>
              </div>

            </div>
          </div>
        </section>

        {/* =================================================
            OVERVIEW
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Overview
            </div>

            <h2 className="cartattva-section-title">
              The challenge, and how we solved it.
            </h2>

            <div className="cartattva-overview-grid">

              <article className="cartattva-info-card">
                <span>THE CHALLENGE</span>

                <h3>
                  Managing a growing automotive
                  inspection operation
                </h3>

                <p>
                  CarTattva needed a centralized platform
                  to manage its growing automotive inspection
                  operations — from customer and dealer
                  acquisition to leads, appointments, field
                  inspections, approvals, reporting and sales
                  follow-ups.
                </p>

                <p>
                  Evaluators also needed to work reliably in
                  locations where internet connectivity was
                  unavailable.
                </p>
              </article>

              <article className="cartattva-info-card solution">

                <span>OUR SOLUTION</span>

                <h3>
                  An integrated web and mobile ecosystem
                </h3>

                <p>
                  HexxaTech developed an integrated web and
                  mobile ecosystem connecting the complete
                  workflow.
                </p>

                <p>
                  The platform manages customers, dealers,
                  leads, appointments and sales operations,
                  while the evaluator mobile application
                  handles field inspections with offline-first
                  data capture and synchronization.
                </p>

                <p>
                  Inspection data then moves through structured
                  approval workflows before automated reports
                  are generated.
                </p>

              </article>

            </div>
          </div>
        </section>

        {/* =================================================
            FEATURES
        ================================================== */}

        <section className="cartattva-features">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Key Features
            </div>

            <h2 className="cartattva-section-title">
              Built around the complete automotive workflow.
            </h2>

            <div className="cartattva-feature-grid">

              {features.map((feature) => (
                <article
                  className="cartattva-feature-card"
                  key={feature.number}
                >

                  <span className="feature-number">
                    {feature.number}
                  </span>

                  <h3>
                    {feature.title}
                  </h3>

                  <p>
                    {feature.text}
                  </p>

                </article>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            TECHNOLOGY
        ================================================== */}

        <section className="cartattva-technology">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Technology
            </div>

            <h2 className="cartattva-section-title">
              What's it built on.
            </h2>

            <p className="technology-description">
              The platform combines a secure web application,
              mobile inspection application and centralized
              database architecture to support field
              operations and business management.
            </p>

            <div className="technology-list">

              {technologies.map((technology) => (
                <span key={technology}>
                  {technology}
                </span>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            BUSINESS IMPACT
        ================================================== */}

        <section className="cartattva-impact">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Business Impact
            </div>

            <h2 className="cartattva-section-title">
              What changed for the business.
            </h2>

            <div className="impact-grid">

              {businessImpact.map((impact) => (
                <div
                  className="impact-item"
                  key={impact}
                >

                  <span className="impact-check">
                    ✓
                  </span>

                  <span>
                    {impact}
                  </span>

                </div>
              ))}

            </div>

          </div>
        </section>

        {/* =================================================
            SCREENSHOTS
        ================================================== */}

        <section className="cartattva-screenshots">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Screenshots
            </div>

            <h2 className="cartattva-section-title">
              See it in action.
            </h2>

            <p className="screenshots-intro">
              Selected screens from the CarTattva platform
              showing the business management, inspection,
              reporting and analytics workflows.
            </p>

            {orderedScreenshots.length > 0 ? (
              <div className="screenshots-grid">

                {orderedScreenshots.map(
                  (screenshot, index) => (
                    <figure
                      className="screenshot-card"
                      key={`${screenshot.fileName}-${index}`}
                    >

                      <div className="screenshot-image-wrapper">

                        <img
                          src={screenshot.image}
                          alt={`${screenshot.title} - CarTattva automotive software platform`}
                          loading={
                            index < 3
                              ? "eager"
                              : "lazy"
                          }
                          decoding="async"
                        />

                      </div>

                      <figcaption>

                        <strong>
                          {screenshot.title}
                        </strong>

                        <span>
                          CarTattva Platform
                        </span>

                      </figcaption>

                    </figure>
                  )
                )}

              </div>
            ) : (
              <div className="no-screenshots">
                <p>
                  CarTattva platform screenshots will
                  appear here.
                </p>
              </div>
            )}

          </div>
        </section>

      {/* =========================================================
    INSPECTION REPORT
========================================================= */}

<section className="inspection-report-section">
  <div className="case-container">

    <div className="section-label">
      INSPECTION REPORT
    </div>

    <h2>
      A professional report generated
      <br />
      from the inspection workflow.
    </h2>

    <div className="inspection-report-card">

      <div className="report-icon">
        PDF
      </div>

      <div className="report-content">

        <span className="report-type">
          Sample Vehicle Inspection Report
        </span>

        <h3>
          CarTattva Inspection Report
        </h3>

        <p>
          Inspection information, vehicle details, evaluation
          results and photographs are consolidated into a
          professional report that can be reviewed and shared
          with the customer.
        </p>

       <div className="report-actions">

  <a
    href={inspectionReport}
    target="_blank"
    rel="noopener noreferrer"
    className="report-view-button"
    onClick={() =>
      trackEvent("inspection_report_view", {
        case_study: "CarTattva",
      })
    }
  >
    View Inspection Report
    <span>↗</span>
  </a>

  <a
    href={inspectionReport}
    download="CarTattva-Inspection-Report.pdf"
    className="report-download-button"
    onClick={() =>
      trackEvent("inspection_report_download", {
        case_study: "CarTattva",
      })
    }
  >
    Download PDF
    <span>↓</span>
  </a>

</div>

      </div>

    </div>

  </div>
</section>


        {/* =================================================
            RELATED SERVICES
        ================================================== */}

        <section className="cartattva-overview">

          <div className="cartattva-container">

            <div className="cartattva-section-label">
              Related Services
            </div>

            <h2 className="cartattva-section-title">
              Services that complement this project.
            </h2>

            <div className="cartattva-feature-grid">

              <article className="cartattva-feature-card">
                <h3>Custom Software Development</h3>
                <p>Business software designed around your actual workflows, approvals and operational requirements.</p>
                <Link to="/services/custom-software-development">Explore Custom Software Development →</Link>
              </article>

              <article className="cartattva-feature-card">
                <h3>CRM & Lead Management</h3>
                <p>Manage leads, sales activity, follow-ups and customer engagement from one connected system.</p>
                <Link to="/services/crm-lead-management">Explore CRM & Lead Management →</Link>
              </article>

              <article className="cartattva-feature-card">
                <h3>Mobile App Development</h3>
                <p>Bring field operations, inspections and customer workflows into a practical mobile experience.</p>
                <Link to="/services/mobile-app-development">Explore Mobile App Development →</Link>
              </article>

            </div>

          </div>
        </section>

        {/* =================================================
            CTA
        ================================================== */}

        <section className="cartattva-cta">

          <div className="cartattva-cta-glow" />

          <div className="cartattva-container">

            <div className="cartattva-cta-inner">

              <span className="cta-label">
                Start Your Project
              </span>

              <h2>
                Have a business problem
                <br />
                that software can solve?
              </h2>

              <p>
                Tell us about your workflow, challenges
                and goals. We'll help you turn them into
                a practical, scalable software solution
                built specifically for your business.
              </p>

              <Link
                to="/contact"
                className="cartattva-cta-button"
                onClick={() =>
                  trackQuoteRequest("cartattva_case_study")
                }
              >
                Discuss Your Project
                <span>→</span>
              </Link>

            </div>

          </div>
        </section>

        {/* =================================================
            FOOTER NOTE
        ================================================== */}

        <div className="cartattva-footer-note">
          Developed by HexxaTech Softwares
        </div>

      </main>
    </>
  );
}
